import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Play } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { ARTIST_NAME, IMG, VIDEOS, YOUTUBE_CHANNEL_URL } from "@/data/content";
import { Reveal, ChapterLabel, PageHero, VideoLightbox, usePageTitle } from "@/components/Shared";

const VideoPage = () => {
  const { t, lf } = useLang();
  const [filter, setFilter] = useState("all");
  const [lightbox, setLightbox] = useState(null);
  usePageTitle(`${t.nav.video} — ${ARTIST_NAME}`);

  const cats = ["all", "quartett", "band", "yello"];
  const filtered = filter === "all" ? VIDEOS : VIDEOS.filter((v) => v.category === filter);

  return (
    <div data-testid="video-page">
      <PageHero eyebrow={t.video.eyebrow} title={t.video.title} img={IMG.video} />

      <section className="px-6 py-20 sm:px-12 sm:py-28 lg:px-20">
        <Reveal>
          <div data-testid="video-filter-bar" className="mb-14 flex flex-wrap gap-3">
            {cats.map((c) => (
              <button
                key={c}
                data-testid={`video-filter-${c}`}
                onClick={() => setFilter(c)}
                className={`border px-6 py-2.5 font-sans text-[10px] font-medium uppercase tracking-[0.3em] transition-all duration-400 ${
                  filter === c
                    ? "border-[#C5A059] bg-[#C5A059] text-[#0B0B0C]"
                    : "border-white/15 text-[#A1A1AA] hover:border-[#C5A059]/50 hover:text-[#C5A059]"
                }`}
              >
                {t.video.categories[c]}
              </button>
            ))}
          </div>
        </Reveal>

        <motion.div layout className="grid gap-8 sm:grid-cols-2 lg:gap-12">
          <AnimatePresence mode="popLayout">
            {filtered.map((v, i) => (
              <motion.div
                layout
                key={v.id}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.96 }}
                transition={{ duration: 0.5, delay: i * 0.06, ease: [0.22, 1, 0.36, 1] }}
                className={i % 2 === 1 ? "lg:mt-16" : ""}
              >
                <button
                  data-testid={`video-thumbnail-${v.id}`}
                  data-cursor="PLAY"
                  onClick={() => setLightbox(v)}
                  className="group relative block aspect-video w-full overflow-hidden text-left"
                >
                  <img src={v.img} alt={v.title} loading="lazy" className="h-full w-full object-cover opacity-75 transition-all duration-700 ease-out group-hover:scale-105 group-hover:opacity-100" />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#0B0B0C]/90 via-transparent to-transparent" />
                  <div className="absolute inset-0 flex items-center justify-center opacity-0 transition-opacity duration-500 group-hover:opacity-100">
                    <span className="flex h-16 w-16 items-center justify-center rounded-full border border-[#C5A059]/70 bg-[#0B0B0C]/60 backdrop-blur">
                      <Play className="ml-1 h-5 w-5 text-[#C5A059]" />
                    </span>
                  </div>
                  <div className="absolute bottom-0 left-0 flex w-full items-end justify-between p-6">
                    <div>
                      <p className="font-mono text-[9px] uppercase tracking-[0.3em] text-[#C5A059]">{t.video.categories[v.category]}</p>
                      <p className="mt-2 font-serif text-xl font-light text-[#F9F8F6] sm:text-2xl">{v.title}</p>
                      <p className="mt-1 font-sans text-xs text-[#A1A1AA]">{lf(v.sub)}</p>
                    </div>
                  </div>
                </button>
              </motion.div>
            ))}
          </AnimatePresence>
        </motion.div>

        <Reveal className="mt-16">
          <a
            href={YOUTUBE_CHANNEL_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="video-youtube-channel-link"
            data-cursor="OPEN"
            className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#71717A] transition-colors hover:text-[#C5A059]"
          >
            {t.video.note} ↗
          </a>
        </Reveal>
      </section>

      <AnimatePresence>{lightbox && <VideoLightbox item={lightbox} onClose={() => setLightbox(null)} />}</AnimatePresence>
    </div>
  );
};

export default VideoPage;
